/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useRef, useState, useEffect } from 'preact/hooks';

/**
 * Hook that exposes a transient "loaded" completion state tied to an external loading flag.
 *
 * Behavior:
 * - When isLoading is true, the hook records that a load is in progress.
 * - When isLoading transitions from true to false, loaded becomes true for 500ms so
 *   consumers can render a short completion/success state, then resets to false.
 */
export function useProgressLoadingState(isLoading: boolean) {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (isLoading && !loaded) {
      setLoading(true);
    }
    if (!isLoading && loading) {
      setLoaded(true);
      setLoading(false);
      timeoutRef.current = setTimeout(() => {
        setLoaded(false);
      }, 500);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isLoading, loaded, loading]);

  return { loaded };
}
